/**
 * Carrying the submitted-enquiry event across the redirect to the thank you page.
 *
 * The enquiry is submitted on the offer page and confirmed on a page of its own, so the MP-04 event has to
 * cross a navigation. It is fired on the thank you page rather than before leaving the offer page, which is
 * where the existing enquiry flow fires its copy (Views/OfferEnquiry/OfferEnquirySent.cshtml): pushing to the
 * data layer and immediately tearing the document down is how a tag ends up half sent.
 *
 * What is stored is the finished event - already hashed, already encrypted - so the thank you page pushes it
 * without needing the customer's answers. No raw email, phone or name is written anywhere.
 *
 * sessionStorage rather than a query string: a URL carrying hashed identifiers would be shared, bookmarked
 * and logged by every proxy in between. It is per tab and dies with it.
 *
 * No aliased imports here, deliberately: jest in this repository has no mapper for the `~/` paths, so a
 * module that reaches for one cannot be unit tested at all.
 */

export const ENQUIRY_HANDOFF_KEY = 'subaru.offerEnquiry.submitted';

/**
 * How long a handed-over event stays valid.
 *
 * A customer who submits an enquiry, leaves the tab open for an hour and then opens the thank you page from
 * their history should not have a second conversion reported. Ten minutes is far longer than the redirect
 * and far shorter than a browsing session.
 */
const MAX_AGE_MS = 10 * 60 * 1000;

export interface EnquiryHandoff {
  /** The offer the enquiry was about, so one offer's event cannot be fired on another's page. */
  offerUniqueName: string;
  /** When it was stored, for the staleness check. */
  at: number;
  /** The finished GA4 event, exactly as it will be pushed. */
  event: Record<string, unknown>;
}

/**
 * Anything that behaves like sessionStorage. Passed in rather than reached for, so this module has no
 * opinion about globals and the tests need no DOM.
 */
export interface HandoffStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

/**
 * Puts the event where the thank you page will look for it.
 *
 * Failure is swallowed: storage throws in a private window and when a quota is full, and losing a
 * measurement must never cost a customer the confirmation of an enquiry that has already been published.
 */
export function stashEnquiryEvent(
  storage: HandoffStorage | undefined,
  handoff: EnquiryHandoff
): void {
  if (!storage) return;

  try {
    storage.setItem(ENQUIRY_HANDOFF_KEY, JSON.stringify(handoff));
  } catch {
    // Nothing to do and nothing to say to the customer.
  }
}

/**
 * Takes the event for this offer, if there is one, and clears it either way.
 *
 * Cleared even when it is stale or for another offer, because a value that was not usable now will not
 * become usable later - leaving it would mean a reload of the thank you page reporting a second enquiry.
 * That "read once" is the whole reason this is a function and not a property.
 */
export function takeEnquiryEvent(
  storage: HandoffStorage | undefined,
  offerUniqueName: string,
  now: number = Date.now()
): Record<string, unknown> | null {
  if (!storage) return null;

  let raw: string | null = null;
  try {
    raw = storage.getItem(ENQUIRY_HANDOFF_KEY);
  } catch {
    return null;
  }

  if (!raw) return null;

  try {
    storage.removeItem(ENQUIRY_HANDOFF_KEY);
  } catch {
    // Reading is what matters; a failed clear is handled by the staleness check above.
  }

  let handoff: EnquiryHandoff;
  try {
    handoff = JSON.parse(raw) as EnquiryHandoff;
  } catch {
    return null;
  }

  if (!handoff || typeof handoff !== 'object') return null;
  if (handoff.offerUniqueName !== offerUniqueName) return null;
  if (typeof handoff.at !== 'number' || now - handoff.at > MAX_AGE_MS) {
    return null;
  }
  if (!handoff.event || typeof handoff.event !== 'object') return null;

  return handoff.event;
}
